// ---- the tracing board ----
// The other way into a word, for a script whose letters are more than one
// shape. A Devanagari akshara is a consonant and the sign that gives it its
// vowel — के is क and े — and a learner handed the whole of के as one chip is
// being asked to learn two shapes as one, and then three or four of those at
// once. So instead of the word's letters in the sky, the round has a board
// with only the word's FIRST letter on it, and only the consonant of that: a
// pale क, a dotted line down the middle of each of its strokes, and a finger
// that shows the way round it. The child draws over it; the ink comes up under
// the finger wherever it is on the letter, and the letter is said once it is
// drawn. If the akshara has a vowel sign, that comes next, in the one colour no
// consonant is ever given (vowels/), and the syllable is said — के — as it is
// asked for and again once it is drawn. Then the letter goes into its place in
// the whole word, the rest of the word comes up round it, and the word is read.
// That is the word done (complete, round.ts), and her moment is the one every
// round ends on.
//
// The board lives in the food's picture. A round opens with the picture and the
// name, as every round does, and the picture breathes with a pencil on it; the
// board comes out of it only when the picture is tapped, and a tap anywhere on
// the scene puts it back in, drawn as far as it was. It covers the top of the
// field, and her serving, his running over and eating are the reward — so the
// child decides when to stop watching them, not the next round.
//
// Arabic is the same three steps with a حركة for the sign: مَ is م and a fatha.
// The word itself is bare, so the fatha is read from its vowelled form (DIA),
// and so is every other letter's: the word that comes up round it is مَوْز,
// as the tray shows it (facesOf, letters.ts), not مَوز. The letter is drawn as
// it stands alone, the way the pop-it and the tray show it, and becomes the
// shape it takes in the word as it lands there.
//
// Nothing here knows a stroke order. The letter is the system's own font, so
// what the finger follows is worked out from the glyph as it is drawn on this
// device: its ink thinned to a one-pixel skeleton (thin, below), the skeleton
// cut into strokes where it branches and joined again where a stroke runs
// straight through a branch, and the strokes put in an order by rule — for
// Devanagari the body left to right and the headline last, for Arabic right to
// left and the dots after. That is the order the finger shows and the guide
// asks for, and it is not enforced: a stroke counts once the finger has passed
// near enough to most of its skeleton, in any order and either way along it,
// and the letter is drawn when nearly all of it is. There is no wrong line on
// the board, only ink that has not come up yet.
import {beep,clips,sayLetter,sayWord,sndPop,speaking,voiceEnd} from './audio';
import {colOf,firstOf,scriptOf,type First} from './letters';
import {complete,round,sayName} from './round';
import {hideHint,promptTaps,revealWord} from './tray';
import {$,clamp} from './util';

export interface TraceCfg{
  own:(k:string)=>boolean;       // is this one of the game's own letters, its clip already in the game's audio/
  borrow:string;                 // where every other syllable's clip is borrowed from: vowels/audio/, by path
}
let cfg:TraceCfg={own:()=>false,borrow:'../vowels/audio/'};
let bound=false;
export function setTrace(c:TraceCfg){
  cfg=c; if(bound)return; bound=true;
  // a tap on the picture brings the board out; once it is out, the picture
  // says the name, as it always has
  promptTaps(()=>{if(waiting())showBoard(performance.now());else sayName();});
  // and a tap on the scene puts it away again, unless a finger is drawing
  $('c').addEventListener('pointerdown',()=>{if(shown&&pid===null&&(stage==='base'||stage==='sign'))hideBoard();});
}

// ---- the first letter, in its parts ----
// What a first letter is made of — its consonant and its sign — is the
// script's business (firstOf, letters.ts), and the pack builder reads it the
// same way (scripts/build-pack.mjs). What is here is how the board draws it.
const arabic=()=>scriptOf().lang.startsWith('ar');
const ZWJ='‍';
// The clips the board says that the game does not already have: every first
// letter's consonant and its syllable, less the ones that are letters of the
// game's own words (those are in its want list already). In Hindi that is the
// bare क of के; in Arabic the مَ after م. scripts/build-pack.mjs lists the same
// clips in a pack's files, by the same cut.
export function traceClips(words:readonly string[]){
  const out:[string,string,string?][]=[], seen=new Set<string>();
  for(const w of words){
    const f=firstOf(w);
    for(const k of [f.base,f.syl])if(!seen.has(k)){seen.add(k);if(!cfg.own(k))out.push(['L',k,cfg.borrow]);}
  }
  return out;
}

// ---- drawing a syllable ----
const MARK='#E6007E', INK='#4a3b30';
const mk=(w:number,h:number)=>{const c=document.createElement('canvas');c.width=Math.max(1,Math.round(w));c.height=Math.max(1,Math.round(h));return c;};
const ctx=(c:HTMLCanvasElement)=>c.getContext('2d')!;
const rtl=()=>document.documentElement.dir==='rtl';
const start=():CanvasTextAlign=>rtl()?'right':'left';
function font(c:CanvasRenderingContext2D,px:number){
  c.font=(arabic()?'bold ':'600 ')+px+'px '+scriptOf().font; c.direction=rtl()?'rtl':'ltr'; c.textBaseline='alphabetic';
}
interface Pt{x:number;y:number}
interface Box{l:number;r:number;t:number;b:number}
const union=(a:Box,b:Box):Box=>({l:Math.min(a.l,b.l),r:Math.max(a.r,b.r),t:Math.min(a.t,b.t),b:Math.max(a.b,b.b)});
const meas=ctx(mk(8,8));
function inkOf(text:string,align:CanvasTextAlign,x:number,px:number):Box{
  font(meas,px); meas.textAlign=align; const m=meas.measureText(text);
  return {l:x-m.actualBoundingBoxLeft,r:x+m.actualBoundingBoxRight,t:-m.actualBoundingBoxAscent,b:m.actualBoundingBoxDescent};
}
// Where the syllable is drawn from, relative to its consonant's origin, at
// size px — the vowels page's own arithmetic. The two share their start edge,
// except under ि, where they share the far one. sylX is where the syllable
// starts, which is where it is drawn from inside the word.
function geom(f:First,text:{base:string;syl:string},px:number){
  const s=start();
  font(meas,px); meas.textAlign=s; const bw=meas.measureText(text.base).width, fw=meas.measureText(text.syl).width;
  const fullAlign:CanvasTextAlign=f.pre?'right':s, fullX=f.pre?bw:0;
  return {fullAlign,fullX,sylX:f.pre?bw-fw:0,ink:union(inkOf(text.base,s,0,px),inkOf(text.syl,fullAlign,fullX,px))};
}
// How far a's ink must move to lie on b's (two canvases the same size): the
// shift, no more than reach device pixels either way, that leaves the most of a
// on ink in b, and the shortest such shift. Tried every few pixels on every few
// of a's pixels, then ever finer round the best. A shift that leaves under 70%
// of a on b has found nothing, and is none.
function fit(a:HTMLCanvasElement,b:HTMLCanvasElement,reach:number):Pt{
  const w=a.width, h=a.height, A=ctx(a).getImageData(0,0,w,h).data, B=ctx(b).getImageData(0,0,w,h).data;
  // b's ink inside a margin wider than any shift tried, so that a's pixels are
  // looked up in it by index alone, with nothing to check
  const step=Math.max(1,Math.ceil(reach/24)), M=Math.ceil(reach)+2*step, P=w+2*M;
  const on=new Uint8Array(P*(h+2*M)), all:number[]=[];
  for(let y=0;y<h;y++)for(let x=0;x<w;x++){
    const i=y*w+x, j=(y+M)*P+x+M;
    on[j]=B[i*4+3]>110?1:0; if(A[i*4+3]>110)all.push(j);
  }
  let best:Pt={x:0,y:0}, top=-1, near=0;
  const look=(n:number,r:number)=>{
    const few=all.filter(j=>j%n===0&&((j/P)|0)%n===0), pts=Int32Array.from(few.length?few:all);
    const x0=best.x, y0=best.y; top=-1;
    for(let dy=y0-r;dy<=y0+r;dy+=n)for(let dx=x0-r;dx<=x0+r;dx+=n){
      const o=dy*P+dx; let k=0;
      for(let i=0;i<pts.length;i++)k+=on[pts[i]+o];
      const d=dx*dx+dy*dy;
      if(k>top||k===top&&d<near){top=k;near=d;best={x:dx,y:dy};}
    }
  };
  if(!all.length)return best;
  let n=step; look(n,Math.floor(reach/n)*n);
  for(let m=n;m>1;m=n){n=m>>2||1;look(n,m);}
  return top>=0.7*all.length?best:{x:0,y:0};
}
// The consonant into one canvas, in its colour, and the sign alone into
// another: the whole syllable with the consonant cut out of it, cut a hair
// wider than it is drawn so no fringe of the sign's colour is left round it.
// Both are device pixels, with the consonant's origin at (ox,oy) in CSS pixels.
// joined is the letter as it is in an Arabic word, held open to the next one.
//
// The consonant and the syllable are two runs of text, and nothing makes a font
// set the consonant in the same place in both: on an iPhone, Geeza Pro sets خُ‍
// a fifth of its size higher than خ‍, and cut where it was asked to be drawn,
// the syllable kept a whole second خ in the sign's colour. So the syllable is
// moved to where its own consonant lies on the consonant's (fit) before it is
// cut. In a word, word is the word as it is drawn, and the consonant and the
// syllable each go where they are in it instead — the one place that is right;
// at is how far the consonant was moved to get there, in device pixels.
function paintSyl(f:First,px:number,ox:number,oy:number,w:number,h:number,dpr:number,joined:boolean,word?:HTMLCanvasElement){
  const text=joined&&arabic()?{base:f.base+ZWJ,syl:f.syl+ZWJ}:{base:f.base,syl:f.syl};
  const G=geom(f,text,px), reach=0.4*px*dpr, s=start(), col=colOf(f.base);
  const run=(t:string,align:CanvasTextAlign,x:number,fill:string,d:Pt)=>{
    const c=mk(w*dpr,h*dpr), k=ctx(c);
    font(k,px*dpr); k.textAlign=align; k.fillStyle=fill; k.fillText(t,x*dpr+d.x,oy*dpr+d.y);
    return c;
  };
  let b=run(text.base,s,ox,col,{x:0,y:0});
  const at=word?fit(b,word,reach):{x:0,y:0};
  if(at.x||at.y)b=run(text.base,s,ox,col,at);
  if(!f.sign)return {base:b,sign:mk(w*dpr,h*dpr),at};
  const m0=run(text.syl,G.fullAlign,ox+G.fullX,MARK,{x:0,y:0});
  let d=word?fit(m0,word,reach):fit(b,m0,reach);
  if(!word)d={x:-d.x,y:-d.y};
  const m=d.x||d.y?run(text.syl,G.fullAlign,ox+G.fullX,MARK,d):m0, mc=ctx(m);
  mc.globalCompositeOperation='destination-out'; mc.textAlign=s; mc.lineWidth=2*dpr; mc.lineJoin='round';
  mc.fillText(text.base,ox*dpr+at.x,oy*dpr+at.y); mc.strokeText(text.base,ox*dpr+at.x,oy*dpr+at.y);
  mc.globalCompositeOperation='source-over';
  return {base:b,sign:m,at};
}

// ---- the skeleton ----
// The glyph's ink, a cell at a time (CELL CSS pixels square), thinned to a line
// one cell wide down the middle of every stroke: peeled from the top, the
// bottom, the right and the left in turn, a cell at a time and only where
// taking it away changes nothing about how the ink holds together (its Yokoi
// number is 1) and does not shorten a line that has already come down to its
// last cell. Zhang–Suen, the usual way to do this, peels a whole side at once
// and so can take a 2×2 block out entirely, and a stroke that thins through
// one is cut in two.
function thin(m:Uint8Array,w:number,h:number){
  const simple=(i:number)=>{
    const x=[i+1,i-w+1,i-w,i-w-1,i-1,i+w-1,i+w,i+w+1].map(j=>1-m[j]);   // E NE N NW W SW S SE, as gaps
    let n=0,b=0;
    for(let k=0;k<8;k++)b+=1-x[k];
    for(let k=0;k<8;k+=2)n+=x[k]-x[k]*x[k+1]*x[(k+2)&7];
    return b>=2&&n===1;
  };
  const cand:number[]=[];
  for(let changed=true;changed;){
    changed=false;
    for(const d of [-w,w,1,-1]){
      cand.length=0;
      for(let y=1;y<h-1;y++)for(let x=1;x<w-1;x++){const i=y*w+x;if(m[i]&&!m[i+d])cand.push(i);}
      for(const i of cand)if(simple(i)){m[i]=0;changed=true;}
    }
  }
}
// The cells a one-cell sliver is made of — what is left of a sign along the
// edge it was cut from — and any speck under `min` cells, gone.
function clean(m:Uint8Array,w:number,h:number,min:number){
  const e=new Uint8Array(w*h);
  for(let y=1;y<h-1;y++)for(let x=1;x<w-1;x++){const i=y*w+x;e[i]=m[i]&m[i-1]&m[i+1]&m[i-w]&m[i+w];}
  m.fill(0);
  for(let y=1;y<h-1;y++)for(let x=1;x<w-1;x++){const i=y*w+x;if(e[i])m[i]=m[i-1]=m[i+1]=m[i-w]=m[i+w]=1;}
  const lab=new Uint8Array(w*h), q:number[]=[], comp:number[]=[];
  for(let i=0;i<w*h;i++){
    if(!m[i]||lab[i])continue;
    comp.length=0; q.push(i); lab[i]=1;
    while(q.length){
      const j=q.pop()!; comp.push(j);
      for(let dy=-w;dy<=w;dy+=w)for(let dx=-1;dx<=1;dx++){const k=j+dy+dx;if(k>=0&&k<w*h&&m[k]&&!lab[k]){lab[k]=1;q.push(k);}}
    }
    if(comp.length<min)for(const j of comp)m[j]=0;
  }
}
// The skeleton as strokes. Its ends and its branch points are nodes (a branch
// point is where three or more runs of ink meet round a cell; a staircase on a
// diagonal is two, and is not one), and a stroke is walked from node to node,
// always on to the cell that bends it least. A short whisker off a branch is
// the thinning's, not the letter's, and is cut; two branch points a stroke's
// width apart are the one junction; and wherever two strokes leave a node in
// nearly opposite directions they are one stroke running through it, as the
// stem of क runs down through the loop that hangs off it. Except on a
// Devanagari headline (headY, in cells; -1 for none), which is drawn on its
// own, after what hangs from it: there only the headline runs through, and a
// stem that meets it, or a sign that rises off it, stops.
function strokesOf(sk:Uint8Array,w:number,h:number,wc:number,headY:number):{p:Pt[];part:number}[]{
  const DX=[0,1,1,1,0,-1,-1,-1], DY=[-1,-1,0,1,1,1,0,-1];
  const on=(x:number,y:number)=>x>=0&&y>=0&&x<w&&y<h&&sk[y*w+x]===1;
  const nbrs=(i:number)=>{const x=i%w,y=(i/w)|0,r:number[]=[];for(let k=0;k<8;k++)if(on(x+DX[k],y+DY[k]))r.push(i+DY[k]*w+DX[k]);return r;};
  const runs=(i:number)=>{const x=i%w,y=(i/w)|0;let a=0;for(let k=0;k<8;k++)if(!on(x+DX[k],y+DY[k])&&on(x+DX[(k+1)&7],y+DY[(k+1)&7]))a++;return a;};
  const isNode=(i:number)=>nbrs(i).length<=1||runs(i)>=3;
  const P=(i:number):Pt=>({x:i%w,y:(i/w)|0});
  const node=new Int32Array(w*h).fill(-1), seen=new Uint8Array(w*h);
  const nodes:{x:number;y:number;n:number}[]=[];
  const addNode=(px:number[])=>{let x=0,y=0;for(const j of px){x+=j%w;y+=(j/w)|0;}nodes.push({x:x/px.length,y:y/px.length,n:px.length});return nodes.length-1;};
  const pix:number[]=[]; for(let i=0;i<w*h;i++)if(sk[i])pix.push(i);
  // which piece of the skeleton each cell is in: a piece no stroke is walked
  // through is a dot
  const part=new Int32Array(w*h).fill(-1), parts:number[][]=[];
  for(const i of pix){
    if(part[i]>=0)continue;
    const id=parts.length, q=[i], px:number[]=[]; part[i]=id;
    while(q.length){const j=q.pop()!;px.push(j);for(const k of nbrs(j))if(part[k]<0){part[k]=id;q.push(k);}}
    parts.push(px);
  }
  for(const i of pix){
    if(node[i]>=0||!isNode(i))continue;
    const id=nodes.length, px:number[]=[], q=[i]; node[i]=id;
    while(q.length){const j=q.pop()!;px.push(j);for(const k of nbrs(j))if(node[k]<0&&isNode(k)){node[k]=id;q.push(k);}}
    addNode(px);
  }
  interface Edge{a:number;b:number;p:Pt[];dead?:boolean}
  const edges:Edge[]=[];
  const walk=(from:number,first:number):Edge=>{
    const p:Pt[]=[{x:nodes[from].x,y:nodes[from].y},P(first)]; seen[first]=1;
    for(let cur=first,steps=1;;steps++){
      const ns=nbrs(cur);
      for(const k of ns){const id=node[k];if(id>=0&&(id!==from||steps>2)){p.push({x:nodes[id].x,y:nodes[id].y});return {a:from,b:id,p};}}
      const a=p[p.length-2], c=p[p.length-1], hx=c.x-a.x, hy=c.y-a.y, hm=Math.hypot(hx,hy)||1;
      let best=-1, bs=-1e9;
      for(const k of ns){
        if(seen[k]||node[k]>=0)continue;
        const dx=k%w-cur%w, dy=((k/w)|0)-((cur/w)|0), s=(dx*hx+dy*hy)/hm/Math.hypot(dx,dy)+(dx&&dy?0:0.01);
        if(s>bs){bs=s;best=k;}
      }
      if(best<0){const q=p[p.length-1];return {a:from,b:addNode([q.y*w+q.x]),p};}   // ran out without meeting a node: its end is one
      seen[best]=1; p.push(P(best)); cur=best;
    }
  };
  const fromNode=(id:number,px:number[])=>{for(const j of px)for(const k of nbrs(j))if(node[k]<0&&!seen[k])edges.push(walk(id,k));};
  const cells=new Map<number,number[]>();
  for(const i of pix)if(node[i]>=0){const l=cells.get(node[i]);if(l)l.push(i);else cells.set(node[i],[i]);}
  for(const [id,px] of cells)fromNode(id,px);
  // what is left is a loop with no end or branch on it (an o), or cells a walk
  // stepped past: each gets a node of its own where it is first met
  for(const i of pix)if(!seen[i]&&node[i]<0){node[i]=addNode([i]);fromNode(node[i],[i]);}
  const len=(e:Edge)=>{let s=0;for(let i=1;i<e.p.length;i++)s+=Math.hypot(e.p[i].x-e.p[i-1].x,e.p[i].y-e.p[i-1].y);return s;};
  const deg=()=>{const d=new Array(nodes.length).fill(0);for(const e of edges)if(!e.dead){d[e.a]++;d[e.b]++;}return d;};
  for(let pass=0;pass<3;pass++){
    const d=deg(); let cut=false;
    for(const e of edges){
      if(e.dead||e.a===e.b)continue;
      const ea=d[e.a]===1, eb=d[e.b]===1;
      if(ea!==eb&&d[ea?e.b:e.a]>=3&&len(e)<0.75*wc){e.dead=true;cut=true;}
    }
    if(!cut)break;
  }
  for(let d=deg(),again=true;again;){
    again=false;
    for(const e of edges){
      if(e.dead||e.a===e.b||d[e.a]<3||d[e.b]<3||len(e)>=0.8*wc)continue;
      e.dead=true; const gone=e.b;
      for(const f of edges)if(!f.dead){if(f.a===gone)f.a=e.a;if(f.b===gone)f.b=e.a;}
      d=deg(); again=true; break;
    }
  }
  // a piece with no stroke left in it once the whiskers are cut (a diamond
  // dot thins to a little cross, all whisker) is a dot too
  const live=edges.filter(e=>!e.dead), walked=new Set<number>();
  for(const e of live){const q=e.p[1];walked.add(part[q.y*w+q.x]);}
  const ends=new Map<number,[number,0|1][]>();
  const put=(n:number,v:[number,0|1])=>{const l=ends.get(n);if(l)l.push(v);else ends.set(n,[v]);};
  live.forEach((e,i)=>{put(e.a,[i,0]);put(e.b,[i,1]);});
  // which way an end leaves its node, read a stroke and a half along it
  const out=(i:number,s:0|1):Pt=>{
    const p=live[i].p, n=p.length, L=Math.min(n-1,Math.max(2,Math.round(1.5*wc)));
    const a=s?p[n-1]:p[0], b=s?p[n-1-L]:p[L], dx=b.x-a.x, dy=b.y-a.y, m=Math.hypot(dx,dy)||1;
    return {x:dx/m,y:dy/m};
  };
  const link=new Map<string,[number,0|1]>(), key=(i:number,s:number)=>i+':'+s;
  for(const [n,list] of ends){
    const onHead=headY>=0&&Math.abs(nodes[n].y-headY)<0.8*wc, flat=(d:Pt)=>Math.abs(d.x)>=0.8;
    const ds=list.map(([i,s])=>out(i,s));
    if(list.length===2){                        // a bend, or a node a walk made: through, but for a corner on the headline
      if(!onHead||(flat(ds[0])&&flat(ds[1]))){link.set(key(...list[0]),list[1]);link.set(key(...list[1]),list[0]);}
      continue;
    }
    if(list.length<3)continue;
    const pairs:{i:number;j:number;c:number}[]=[];
    for(let i=0;i<list.length;i++)for(let j=i+1;j<list.length;j++)
      if(!onHead||(flat(ds[i])&&flat(ds[j])))pairs.push({i,j,c:ds[i].x*ds[j].x+ds[i].y*ds[j].y});
    pairs.sort((a,b)=>a.c-b.c);
    const used=new Set<number>();
    for(const {i,j,c} of pairs){
      if(c>-0.55)break;
      if(used.has(i)||used.has(j))continue;
      used.add(i); used.add(j); link.set(key(...list[i]),list[j]); link.set(key(...list[j]),list[i]);
    }
  }
  const done=new Uint8Array(live.length), strokes:{p:Pt[];part:number}[]=[];
  const run=(i:number,s:0|1)=>{
    const pts:Pt[]=[];
    for(let ci=i,cs=s;!done[ci];){
      done[ci]=1;
      const p=cs===0?live[ci].p:live[ci].p.slice().reverse();
      pts.push(...(pts.length?p.slice(1):p));
      const nx=link.get(key(ci,cs===0?1:0)); if(!nx)break;
      [ci,cs]=nx;
    }
    return pts;
  };
  // free ends first, so that a stroke is walked from one end to the other; what
  // is left after that is closed loops
  const partOf=(i:number)=>{const q=live[i].p[1];return part[q.y*w+q.x];};
  for(let i=0;i<live.length;i++)for(const s of [0,1] as const)if(!done[i]&&!link.has(key(i,s)))strokes.push({part:partOf(i),p:run(i,s)});
  for(let i=0;i<live.length;i++)if(!done[i])strokes.push({part:partOf(i),p:run(i,0)});
  // a dot, thinned to a cell or two that neither run nor branch: a point to touch
  parts.forEach((px,i)=>{if(walked.has(i))return;let x=0,y=0;for(const j of px){x+=j%w;y+=(j/w)|0;}strokes.push({part:i,p:[{x:x/px.length,y:y/px.length}]});});
  return strokes;
}
function smooth(p:Pt[]){
  if(p.length<5)return p;
  const o=p.map(q=>({...q}));
  for(let i=2;i<p.length-2;i++)o[i]={x:(p[i-2].x+p[i-1].x+p[i].x+p[i+1].x+p[i+2].x)/5,y:(p[i-2].y+p[i-1].y+p[i].y+p[i+1].y+p[i+2].y)/5};
  return o;
}
function resample(p:Pt[],step:number){
  if(p.length<2)return p;
  const o=[p[0]]; let acc=0;
  for(let i=1;i<p.length;i++){
    let a=p[i-1], d=Math.hypot(p[i].x-a.x,p[i].y-a.y);
    while(acc+d>=step){const k=(step-acc)/d, q={x:a.x+(p[i].x-a.x)*k,y:a.y+(p[i].y-a.y)*k};o.push(q);a=q;d=Math.hypot(p[i].x-a.x,p[i].y-a.y);acc=0;}
    acc+=d;
  }
  const z=p[p.length-1], y=o[o.length-1];
  if(Math.hypot(z.x-y.x,z.y-y.y)>step*0.3)o.push(z);
  return o;
}
const lenOf=(s:Pt[])=>{let d=0;for(let i=1;i<s.length;i++)d+=Math.hypot(s[i].x-s[i-1].x,s[i].y-s[i-1].y);return d;};
// The strokes in the order the hand takes them, each pointing the way it goes.
// Devanagari: what hangs below the headline first, left to right, a stem from
// the top and anything flatter from the left; then the headline, from the left;
// then whatever rises above it (the flick of े, the dot of ं). Arabic: right to
// left, a flat stroke from its right end, the dots after.
function order(ss:Pt[][],box:Box,wide:number,headY:number):Pt[][]{
  const R=rtl(), deva=!arabic(), bw=box.r-box.l, bh=box.b-box.t;
  const info=ss.map(s=>{
    let l=1e9,r=-1e9,t=1e9,b=-1e9,my=0;
    for(const p of s){l=Math.min(l,p.x);r=Math.max(r,p.x);t=Math.min(t,p.y);b=Math.max(b,p.y);my+=p.y/s.length;}
    const w=r-l, h=b-t, dot=s.length===1;
    const head=deva&&!dot&&(headY>=0?h<0.5*w&&Math.abs(my-headY)<0.8*wide:h<0.35*w&&w>0.22*bw&&t-box.t<0.28*bh);
    const tier=head?1:deva&&headY>=0&&my<headY-0.5*wide?2:dot?1.5:0;   // below, the headline, above; Arabic's dots after its body
    let p=s;
    if(!dot){
      const a=s[0], z=s[s.length-1];
      if(Math.hypot(a.x-z.x,a.y-z.y)>=wide||lenOf(s)<3*wide){   // a closed loop starts where it was found
        const flip=head||w>h*1.25?(R?a.x<z.x:a.x>z.x):a.y>z.y;
        if(flip)p=s.slice().reverse();
      }
    }
    return {p,l,r,t,tier};
  });
  const along=(i:number)=>R?-info[i].r:info[i].l;
  return info.map((_,i)=>i).sort((i,j)=>{
    const A=info[i], B=info[j];
    if(A.tier!==B.tier)return A.tier-B.tier;
    const d=along(i)-along(j);
    return Math.abs(d)>0.12*bw?d:A.t-B.t;
  }).map(i=>info[i].p);
}
// A layer's glyph (device pixels) as strokes in CSS pixels, how wide its
// strokes are, the box its ink fills, and where its headline runs. A cell is a
// hundred-and-tenth of the font size, so the thinnest stroke any face here
// draws is several cells wide.
//
// The headline is found in the consonant, as the rows near its top with the
// longest unbroken run of ink — a Devanagari letter's top bar runs nearly its
// whole width, and nothing else in it does — and handed to its sign, which may
// carry only a stub of it.
function analyse(src:HTMLCanvasElement,W:number,H:number,px:number,head?:number){
  const CELL=clamp(px/110,1,3), w=Math.ceil(W/CELL)+2, h=Math.ceil(H/CELL)+2;
  const c=mk(w,h).getContext('2d',{willReadFrequently:true})!;
  c.imageSmoothingQuality='high'; c.drawImage(src,1,1,W/CELL,H/CELL);
  const d=c.getImageData(0,0,w,h).data, m=new Uint8Array(w*h);
  for(let i=0;i<w*h;i++)m[i]=d[i*4+3]>110?1:0;
  clean(m,w,h,6);
  let area=0,l=w,r=0,t=h,b=0;
  for(let i=0;i<w*h;i++)if(m[i]){area++;const x=i%w,y=(i/w)|0;l=Math.min(l,x);r=Math.max(r,x);t=Math.min(t,y);b=Math.max(b,y);}
  if(!area)return {strokes:[] as Pt[][],wide:0,box:{l:0,r:0,t:0,b:0},head:-1};
  let hy=head!==undefined&&head>=0?head/CELL+0.5:-1;
  if(head===undefined&&!arabic()){
    const run:number[]=[];
    for(let y=t;y<=t+(b-t)*0.4;y++){let best=0,k=0;for(let x=l;x<=r;x++){k=m[y*w+x]?k+1:0;best=Math.max(best,k);}run.push(best);}
    const top=Math.max(...run);
    if(top>=0.35*(r-l+1)){let s=0,n=0;run.forEach((v,i)=>{if(v>=0.85*top){s+=t+i;n++;}});hy=s/n;}
  }
  thin(m,w,h);
  let n=0; for(let i=0;i<w*h;i++)n+=m[i];
  const wc=Math.max(1.5,area/Math.max(1,n)), wide=wc*CELL;
  const toPx=(p:Pt)=>({x:(p.x-0.5)*CELL,y:(p.y-0.5)*CELL});
  const box={l:(l-1)*CELL,r:r*CELL,t:(t-1)*CELL,b:b*CELL};
  // A piece too short to draw is a dot to touch when it is all there is of its
  // part of the letter (ं), and nothing at all when it is a sliver where two
  // strokes meet.
  const raw=strokesOf(m,w,h,wc,hy).map(s=>({part:s.part,p:resample(smooth(s.p.map(toPx)),4)}));
  const short=(q:Pt[])=>q.length===1||lenOf(q)<0.8*wide;
  const ss=raw.filter(s=>!short(s.p)||!raw.some(o=>o!==s&&o.part===s.part&&!short(o.p)))
    .map(s=>short(s.p)?[s.p[s.p.length>>1]]:s.p);
  const headPx=hy>=0?(hy-0.5)*CELL:-1;
  return {strokes:order(ss,box,wide,headPx),wide,box,head:headPx};
}

// ---- the board ----
const board=document.createElement('div'), cv=document.createElement('canvas'), g=ctx(cv);
board.id='trace'; board.appendChild(cv); $('ui').insertBefore(board,$('dbg'));
interface Layer{
  col:string; glyph:HTMLCanvasElement; paint:HTMLCanvasElement; ink:HTMLCanvasElement;
  strokes:Pt[][]; cov:Uint8Array[]; total:number; got:number; dots:Pt[]; box:Box; wide:number;
  R:number;          // how near the finger must pass a point of the skeleton to have drawn it
  brush:number;      // how wide the ink comes up under it
  trail:Pt[][];      // every stroke the finger has made on it, in font sizes from the letter's origin, to lay out again
  done:boolean; doneT:number; shownT:number; pops:number;
  head:number;       // where its headline runs, in CSS px down the board; -1 for none
}
type Stage='off'|'base'|'sign'|'word'|'gone';
let stage:Stage='off', word='', cur:First|null=null, openT=0, shown=false;
let W=0, dpr=1, seenW=-1, seenH=-1, built=false;
let px=0, ox=0, oy=0, sylX=0;        // the syllable on the board: its size, its consonant's origin, where it starts
let base:Layer|null=null, sign:Layer|null=null;
let wordT=0, rest:HTMLCanvasElement|null=null, inWord:ReturnType<typeof paintSyl>|null=null, P2=0, O2:Pt={x:0,y:0};
let land:Pt={x:0,y:0};              // how far from where it was asked to go the letter is in the word, as the word is drawn
let saidT=-1e9, touchT=-1e9;

function layer(glyph:HTMLCanvasElement,col:string,trail:Pt[][],done:boolean,shownT:number,head?:number):Layer{
  const A=analyse(glyph,W,W,px,head), wide=A.wide||px*0.08;
  const L:Layer={col,glyph,paint:mk(glyph.width,glyph.height),ink:mk(glyph.width,glyph.height),strokes:A.strokes,
    cov:A.strokes.map(s=>new Uint8Array(s.length)),total:A.strokes.reduce((n,s)=>n+s.length,0),got:0,dots:[],box:A.box,wide,
    R:Math.max(16,wide*0.5+12),brush:Math.max(12,wide*0.6+6),trail:[],done,doneT:done?-1e9:0,shownT,pops:0,head:A.head};
  for(const s of L.strokes){                   // the dotted line down the middle, a dot every 12px
    let acc=12;
    for(let i=1;i<s.length;i++){acc+=Math.hypot(s[i].x-s[i-1].x,s[i].y-s[i-1].y);if(acc>=12){L.dots.push(s[i]);acc=0;}}
    if(s.length===1)L.dots.push(s[0]);
  }
  for(const t of trail){                       // what was drawn before a resize, drawn again
    const q=t.map(p=>({x:ox+p.x*px,y:oy+p.y*px}));
    L.trail.push(t);
    for(let i=0;i<q.length;i++)ink(L,q[Math.max(0,i-1)],q[i]);
  }
  if(!L.total)L.done=true;                     // nothing to draw (no font for it): nothing to ask for
  return L;
}
// Where the board goes. Upright, across the top, down to the tray; on its side,
// a square at the reading end of the screen (left in Hindi, right in Arabic),
// clear of the tray in the middle, with her and her table beside it.
function place(){
  const vw=innerWidth, vh=innerHeight; if(!vw||!vh)return false;
  const land=vw>vh*1.15, trayTop=$('tray').getBoundingClientRect().top||vh*0.8;
  let s:number, x:number, y:number;
  if(land){s=Math.min(vh-76,vw/2-76,560);x=rtl()?vw-s-16:16;y=Math.max(64,(vh-s)/2);}
  else{s=Math.min(vw-28,trayTop-86,560);x=(vw-s)/2;y=70;}
  s=Math.max(140,s);
  const pr=$('prompt').getBoundingClientRect();   // it grows out of the picture and goes back into it
  Object.assign(board.style,{left:x+'px',top:y+'px',width:s+'px',height:s+'px',
    transformOrigin:(pr.left+pr.width/2-x)+'px '+(pr.top+pr.height/2-y)+'px'});
  W=s; dpr=Math.min(2,devicePixelRatio||1); cv.width=cv.height=Math.round(s*dpr);
  seenW=vw; seenH=vh; return true;
}
function build(){
  if(!cur||!place())return false;
  const f=cur, G=geom(f,f,100), u=G.ink, k=Math.min(W*0.80/(u.r-u.l),W*0.74/(u.b-u.t));
  px=100*k; ox=W/2-(u.l+u.r)/2*k; oy=W/2-(u.t+u.b)/2*k; sylX=G.sylX*k;
  const c=paintSyl(f,px,ox,oy,W,W,dpr,false);
  base=layer(c.base,colOf(f.base),base?.trail??[],base?.done??false,base?.shownT??performance.now());
  sign=f.sign?layer(c.sign,MARK,sign?.trail??[],sign?.done??false,sign?.shownT??0,base.head):null;
  board.style.setProperty('--c',colOf(f.base));
  if(stage==='word'||stage==='gone')buildWord();
  built=true; return true;
}
// The word at size P from (x,y), on a w×h canvas at d device pixels to the CSS
// one: the rest of it in ink, with a hole where its first letter goes, and that
// letter as it is in the word, in its own colours, to land in the hole — put
// where the word as drawn has it (paintSyl).
function paintWord(f:First,P:number,x:number,y:number,w:number,h:number,d:number){
  const G=geom(f,arabic()?{base:f.base+ZWJ,syl:f.syl+ZWJ}:f,P);
  const rest=mk(w*d,h*d), r=ctx(rest);
  font(r,P*d); r.textAlign=start(); r.fillStyle=INK; r.fillText(f.word,x*d,y*d);
  const syl=paintSyl(f,P,x-G.sylX,y,w,h,d,true,rest);
  r.globalCompositeOperation='destination-out';
  r.drawImage(syl.base,0,0); r.drawImage(syl.sign,0,0);
  r.globalCompositeOperation='source-over';
  return {rest,syl};
}
// the word, laid out to fill the board's width
function buildWord(){
  if(!cur)return;
  const b=inkOf(cur.word,start(),0,100), k=Math.min(W*0.86/(b.r-b.l),W*0.40/(b.b-b.t));
  P2=100*k; O2={x:W/2-(b.l+b.r)/2*k,y:W/2-(b.t+b.b)/2*k};
  ({rest,syl:inWord}=paintWord(cur,P2,O2.x,O2.y,W,W,dpr)); land={x:inWord.at.x/dpr,y:inWord.at.y/dpr};
}
// The word as the board ends on it, in one piece, for the tray to keep
// (revealWord, tray.ts): the board's own drawing of it, so it comes down joined
// up as it was made and not as a slot per letter. It is drawn again at about
// the size the tray shows it (its ink 60px high), not shrunk from the board's,
// so that it is as sharp there.
function wordPic(f:First){
  const H=60, m=8, b=inkOf(f.word,start(),0,100), k=H/(b.b-b.t), d=Math.min(3,devicePixelRatio||1);
  const w=(b.r-b.l)*k+2*m, h=H+2*m, {rest,syl}=paintWord(f,100*k,m-b.l*k,m-b.t*k,w,h,d);
  const c=mk(w*d,h*d), x=ctx(c);
  x.drawImage(rest,0,0); x.drawImage(syl.base,0,0); x.drawImage(syl.sign,0,0);
  return c;
}

// ---- the finger ----
const active=()=>stage==='base'?base:stage==='sign'?sign:null;
// ink comes up under the finger from a to b, and every point of the skeleton it
// passes near enough is drawn
function ink(L:Layer,a:Pt,b:Pt){
  const p=ctx(L.paint); p.lineCap='round'; p.lineJoin='round'; p.strokeStyle='#000'; p.lineWidth=2*L.brush*dpr;
  p.beginPath(); p.moveTo(a.x*dpr,a.y*dpr); p.lineTo(b.x*dpr,b.y*dpr); p.stroke();
  const n=Math.max(1,Math.ceil(Math.hypot(b.x-a.x,b.y-a.y)/(L.R/3))), R2=L.R*L.R;
  for(let j=0;j<=n;j++){
    const x=a.x+(b.x-a.x)*j/n, y=a.y+(b.y-a.y)*j/n;
    L.strokes.forEach((s,si)=>{const c=L.cov[si];for(let i=0;i<s.length;i++)if(!c[i]&&(s[i].x-x)**2+(s[i].y-y)**2<=R2){c[i]=1;L.got++;}});
  }
  const k=ctx(L.ink); k.globalCompositeOperation='copy'; k.drawImage(L.paint,0,0);
  k.globalCompositeOperation='source-in'; k.drawImage(L.glyph,0,0); k.globalCompositeOperation='source-over';
}
const strokeCov=(L:Layer,i:number)=>{const c=L.cov[i];let n=0;for(const v of c)n+=v;return n/c.length;};
// drawn: nearly all of it, and at least half of every stroke, so that the
// stem alone, wide as the finger is, never passes for the whole letter
const drawn=(L:Layer)=>L.got>=0.85*L.total&&L.cov.every((_,i)=>strokeCov(L,i)>=0.5);
let pid:number|null=null, last:Pt|null=null, on:Layer|null=null;
const local=(e:PointerEvent):Pt=>{const r=cv.getBoundingClientRect();return {x:(e.clientX-r.left)*W/(r.width||1),y:(e.clientY-r.top)*W/(r.height||1)};};
const norm=(p:Pt):Pt=>({x:(p.x-ox)/px,y:(p.y-oy)/px});
cv.addEventListener('pointerdown',e=>{
  e.preventDefault(); e.stopPropagation(); hideHint();
  if(pid!==null||!built)return;
  const t=performance.now(), p=local(e); pid=e.pointerId; touchT=t; demo=null;
  try{cv.setPointerCapture(pid);}catch(err){}
  burst(p.x,p.y,active()?.col??colOf(cur?.base??''),4,0.5);
  // every touch is answered: the sound of what is on the board, once the voice is free
  if(!speaking(t)&&t-saidT>4000)say(stage==='word'?null:stage==='sign'?cur!.syl:cur!.base,t);
  on=active(); last=p;
  if(on&&!on.done){on.trail.push([norm(p)]);stroke(on,p,p,t);}
});
cv.addEventListener('pointermove',e=>{
  if(e.pointerId!==pid||!on||!last)return;
  const t=performance.now(); touchT=t;
  const evs=e.getCoalescedEvents?.();
  for(const ev of evs&&evs.length?evs:[e]){
    if(on.done)break;
    const p=local(ev); stroke(on,last,p,t); on.trail[on.trail.length-1].push(norm(p)); last=p;
  }
});
const lift=(e:PointerEvent)=>{if(e.pointerId!==pid)return;pid=null;last=null;on=null;touchT=performance.now();};
cv.addEventListener('pointerup',lift); cv.addEventListener('pointercancel',lift);
function stroke(L:Layer,a:Pt,b:Pt,t:number){
  if(L.done)return;
  ink(L,a,b);
  const k=Math.floor(L.got/L.total*8);        // a rising pop every eighth of the way
  if(k>L.pops){L.pops=k;sndPop(k);}
  if(drawn(L))finish(L,t);
}

// ---- what is said, and when ----
// Each step waits for the voice to be free and then a beat more (and, for the
// first, for the round to have said the word's name), so that nothing the
// board says talks over anybody — and runs anyway if the wait goes on past
// all reason, so that a clip that never comes cannot hold the round up.
interface Step{wait:number;until?:()=>boolean;fn:(t:number)=>void;at:number}
let steps:Step[]=[], readyAt=0, intro=false;
const later=(wait:number,fn:(t:number)=>void,until?:()=>boolean)=>{steps.push({wait,fn,until,at:performance.now()});};
function runSteps(t:number){
  const s=steps[0]; if(!s)return;
  const from=Math.max(readyAt,s.at), due=t>=Math.max(from,voiceEnd())+s.wait&&(s.until?.()??true);
  if(!due&&t<from+s.wait+8000)return;
  steps.shift(); s.fn(t); readyAt=performance.now();
}
const loaded=(k:string)=>()=>clips[k]!==undefined;
function say(k:string|null,t:number){saidT=t;if(k===null)sayWord(word,true);else sayLetter(k);}

// A new round's board: its first letter's consonant, pale, waiting in the
// picture to be drawn. Called where the round would have dealt its chips
// (deal, round.ts).
export function openTrace(w:string,t:number){
  word=w; cur=firstOf(w); stage='base'; openT=t; steps=[]; readyAt=t; demo=null; parts.length=0;
  base=sign=null; rest=inWord=null; built=false; shown=false; intro=true;
  board.classList.remove('on','out'); invite(true);
}
const waiting=()=>!shown&&(stage==='base'||stage==='sign');
const invite=(on:boolean)=>{$('prompt').classList.toggle('draw',on);};
// Out of the picture: the first time, the consonant is said and the finger
// shows the whole of it; after that, what was being drawn is said again and the
// finger shows the next stroke — unless something was already queued to be said.
function showBoard(t:number){
  if(!waiting()||!build())return;
  shown=true; invite(false); hideHint(); openT=readyAt=touchT=t;
  board.classList.remove('out'); board.classList.add('on');
  if(steps.length)return;
  if(intro)later(250,t=>{intro=false;if(t-saidT>1500)say(cur!.base,t);if(base&&!base.done)show(base,'all',t);},
    ()=>!round.owed&&loaded('L'+cur!.base)());
  else later(200,t=>{const A=active();say(stage==='sign'?cur!.syl:cur!.base,t);if(A&&!A.done)show(A,'next',t);});
}
// back into the picture, with what was drawn kept and anything still to be said held
function hideBoard(){shown=false;demo=null;pid=null;on=null;last=null;board.classList.remove('on');invite(true);}
function finish(L:Layer,t:number){
  L.done=true; L.doneT=t; demo=null;
  beep(660,990,.12,'triangle',.14); beep(990,1320,.14,'triangle',.12,.1);
  burst((L.box.l+L.box.r)/2,(L.box.t+L.box.b)/2,L.col,14,1);
  if(L===base){
    if(!intro)later(150,t=>say(cur!.base,t));
    if(sign)later(350,t=>{stage='sign';sign!.shownT=t;say(cur!.syl,t);show(sign!,'all',t);},loaded('L'+cur!.syl));
    else later(450,toWord);
  }else{later(150,t=>say(cur!.syl,t));later(400,toWord);}
}
// The letter goes into its place in the word, and the rest of the word comes up
// round it; the word is read; and then it is done, and comes down into the tray
// as she serves it.
function toWord(t:number){
  stage='word'; wordT=t; buildWord();
  later(900,t=>say(null,t),loaded('W'+word));
  later(350,t=>{stage='gone';revealWord(word,wordPic(cur!));complete(t);board.classList.add('out');});
}

// ---- the finger that shows the way ----
// Round every stroke of a layer in order as it is first asked for, and round
// the next stroke still to draw whenever the board has been left alone a while.
let demo:{L:Layer;list:number[];i:number;t0:number;end:number}|null=null;
const SPEED=0.16;                               // px a millisecond: a 250px stroke in a second and a half, slow enough to follow
function show(L:Layer,which:'all'|'next',t:number){
  const n=next(L); if(n<0)return;
  demo={L,list:which==='all'?L.strokes.map((_,i)=>i):[n],i:0,t0:t+450,end:0};
}
const next=(L:Layer)=>L.cov.findIndex((_,i)=>strokeCov(L,i)<0.8);

// ---- sparkles ----
interface Spark{x:number;y:number;vx:number;vy:number;t0:number;col:string;r:number}
const parts:Spark[]=[], COLS=['#FFD23F','#FF7BAC','#7CE26B','#6FD3FF','#C084FC','#FF9F43'];
function burst(x:number,y:number,col:string,n:number,big:number){
  const t=performance.now();
  for(let i=0;i<n;i++){const a=i/n*6.283+Math.random()*0.6, v=(0.08+Math.random()*0.18)*(0.6+big);
    parts.push({x,y,vx:Math.cos(a)*v,vy:Math.sin(a)*v,t0:t,col:i%3?COLS[i%COLS.length]:col,r:(3+Math.random()*4)*(0.6+big*0.6)});}
}

// ---- the frame ----
const ease=(k:number)=>k<0.5?2*k*k:1-(-2*k+2)**2/2;
function line(c:CanvasRenderingContext2D,s:Pt[],upto:number){
  c.beginPath(); c.moveTo(s[0].x*dpr,s[0].y*dpr); let d=0;
  for(let i=1;i<s.length;i++){
    const seg=Math.hypot(s[i].x-s[i-1].x,s[i].y-s[i-1].y);
    if(d+seg>=upto){const k=(upto-d)/seg;c.lineTo((s[i-1].x+(s[i].x-s[i-1].x)*k)*dpr,(s[i-1].y+(s[i].y-s[i-1].y)*k)*dpr);break;}
    d+=seg; c.lineTo(s[i].x*dpr,s[i].y*dpr);
  }
  c.stroke();
}
function at(s:Pt[],upto:number):Pt{
  let d=0;
  for(let i=1;i<s.length;i++){const seg=Math.hypot(s[i].x-s[i-1].x,s[i].y-s[i-1].y);if(d+seg>=upto){const k=(upto-d)/seg;return {x:s[i-1].x+(s[i].x-s[i-1].x)*k,y:s[i-1].y+(s[i].y-s[i-1].y)*k};}d+=seg;}
  return s[s.length-1];
}
function fingerAt(p:Pt,col:string,r:number,a=1){
  g.globalAlpha=a; g.fillStyle='rgba(0,0,0,.18)'; g.beginPath(); g.arc(p.x*dpr+2*dpr,p.y*dpr+3*dpr,r*dpr,0,6.283); g.fill();
  g.fillStyle='#fff'; g.beginPath(); g.arc(p.x*dpr,p.y*dpr,r*dpr,0,6.283); g.fill();
  g.lineWidth=3.5*dpr; g.strokeStyle=col; g.stroke(); g.globalAlpha=1;
}
function drawLayer(L:Layer,t:number,isActive:boolean){
  const fade=clamp((t-L.shownT)/300,0,1);
  if(!L.done){
    g.globalAlpha=0.17*fade; g.drawImage(L.glyph,0,0); g.globalAlpha=1;
    if(isActive){g.fillStyle=L.col;g.globalAlpha=0.5*fade;const r=Math.max(1.8,L.wide*0.08)*dpr;
      for(const d of L.dots){g.beginPath();g.arc(d.x*dpr,d.y*dpr,r,0,6.283);g.fill();} g.globalAlpha=1;}
    g.drawImage(L.ink,0,0);
  }else{
    const k=clamp((t-L.doneT)/280,0,1);
    g.drawImage(L.ink,0,0); g.globalAlpha=k; g.drawImage(L.glyph,0,0); g.globalAlpha=1;
  }
}
function draw(t:number){
  g.setTransform(1,0,0,1,0,0); g.clearRect(0,0,cv.width,cv.height);
  if(!base)return;
  if(stage==='word'||stage==='gone'){
    const k=ease(clamp((t-wordT)/700,0,1)), s=1+(P2/px-1)*k, S={x:ox+sylX,y:oy}, T={x:O2.x+land.x,y:O2.y+land.y};
    const fadeOld=1-clamp((t-wordT-700)/250,0,1);
    g.globalAlpha=clamp((t-wordT-300)/500,0,1); if(rest)g.drawImage(rest,0,0);
    g.globalAlpha=fadeOld;
    g.setTransform(s,0,0,s,(S.x+(T.x-S.x)*k-S.x*s)*dpr,(S.y+(T.y-S.y)*k-S.y*s)*dpr);
    g.drawImage(base.glyph,0,0); if(sign)g.drawImage(sign.glyph,0,0);
    g.setTransform(1,0,0,1,0,0);
    g.globalAlpha=1-fadeOld; if(inWord){g.drawImage(inWord.base,0,0);g.drawImage(inWord.sign,0,0);}
    g.globalAlpha=1;
  }else{
    const A=active();
    drawLayer(base,t,A===base);
    if(sign&&stage==='sign')drawLayer(sign,t,A===sign);
    if(A&&!A.done){
      if(demo&&demo.L===A){
        const s=A.strokes[demo.list[demo.i]], L=lenOf(s), d=Math.max(0,(t-demo.t0)*SPEED);
        g.lineCap='round'; g.lineJoin='round'; g.strokeStyle=A.col; g.lineWidth=A.wide*0.5*dpr; g.globalAlpha=0.35;
        for(let j=0;j<demo.i;j++)line(g,A.strokes[demo.list[j]],1e9);
        if(s.length>1&&d>0)line(g,s,d);
        g.globalAlpha=1;
        const head=s.length>1?at(s,d):s[0], pulse=s.length>1?1:1+0.25*Math.sin((t-demo.t0)*0.012);
        if(t>=demo.t0-250)fingerAt(head,A.col,Math.max(11,A.wide*0.42)*pulse);
        if(d>=(s.length>1?L:110)){demo.i++;demo.t0=t+380;if(demo.i>=demo.list.length)demo=null;}
      }else if(pid===null){
        const n=next(A);
        if(n>=0){
          const s=A.strokes[n], i0=Math.max(0,A.cov[n].findIndex(v=>!v)), p=s[i0], b=0.5+0.5*Math.sin(t*0.008);
          g.globalAlpha=0.35+0.4*b; g.strokeStyle=A.col; g.lineWidth=4*dpr;
          g.beginPath(); g.arc(p.x*dpr,p.y*dpr,(A.wide*0.45+8+6*b)*dpr,0,6.283); g.stroke(); g.globalAlpha=1;
          if(s.length>1){                       // which way to go from there
            const q=s[Math.min(s.length-1,i0+4)], a=Math.atan2(q.y-p.y,q.x-p.x), r=(A.wide*0.45+20)*dpr;
            const hx=p.x*dpr+Math.cos(a)*r, hy=p.y*dpr+Math.sin(a)*r;
            g.fillStyle=A.col; g.globalAlpha=0.55+0.35*b; g.beginPath();
            g.moveTo(hx+Math.cos(a)*9*dpr,hy+Math.sin(a)*9*dpr);
            g.lineTo(hx+Math.cos(a+2.4)*9*dpr,hy+Math.sin(a+2.4)*9*dpr);
            g.lineTo(hx+Math.cos(a-2.4)*9*dpr,hy+Math.sin(a-2.4)*9*dpr); g.fill(); g.globalAlpha=1;
          }
        }
      }
    }
  }
  for(let i=parts.length-1;i>=0;i--){
    const p=parts[i], a=(t-p.t0)/650;
    if(a>=1){parts.splice(i,1);continue;}
    const dt=t-p.t0;
    g.globalAlpha=1-a; g.fillStyle=p.col; g.beginPath();
    g.arc((p.x+p.vx*dt)*dpr,(p.y+p.vy*dt+0.00012*dt*dt)*dpr,p.r*(1-a*0.6)*dpr,0,6.283); g.fill();
  }
  g.globalAlpha=1;
}
// The board's frame, from the game's loop: a resize lays it out again, the
// steps run, the finger shows the way after a quiet spell, and it is drawn.
export function updateTrace(t:number){
  if(stage==='off'||!shown)return;
  if(!built||innerWidth!==seenW||innerHeight!==seenH){if(!build())return;}
  runSteps(t);
  const A=active();
  if(A&&!A.done&&!demo&&pid===null&&t-Math.max(touchT,A.shownT,openT)>6000&&t-saidT>2000){show(A,'next',t);touchT=t;}
  if(!frozen)draw(t);
  if(stage==='gone'&&t-wordT>4000){stage='off';shown=false;board.classList.remove('on','out');}
}
// The ?dbg handle's view of the board, and a finger that draws the layer being
// asked for along its own strokes, for a headless run.
let frozen=false;
export const traceDebug={
  // the board opened on any word, and its strokes drawn over it numbered in
  // the order they are asked for, a start dot on each, held until unfrozen
  open:(w:string)=>{openTrace(w,performance.now());showBoard(performance.now());frozen=false;},
  show:()=>showBoard(performance.now()),hide:()=>hideBoard(),get shown(){return shown},
  set frozen(v:boolean){frozen=v;},
  map(which:'base'|'sign'){
    const L=which==='base'?base:sign; if(!L)return null; frozen=true;
    const C=['#e6194b','#3cb44b','#4363d8','#f58231','#911eb4','#42d4f4','#f032e6','#469990','#9A6324','#000075'];
    g.setTransform(1,0,0,1,0,0); g.clearRect(0,0,cv.width,cv.height);
    g.globalAlpha=0.25; g.drawImage(L.glyph,0,0); g.globalAlpha=1;
    L.strokes.forEach((s,i)=>{
      const c=C[i%C.length]; g.strokeStyle=c; g.fillStyle=c; g.lineWidth=4*dpr; g.lineCap='round';
      if(s.length>1)line(g,s,1e9);
      g.beginPath(); g.arc(s[0].x*dpr,s[0].y*dpr,6*dpr,0,6.283); g.fill();
      g.font=(15*dpr)+'px sans-serif'; g.fillText(String(i+1),(s[0].x+8)*dpr,(s[0].y-8)*dpr);
    });
    return L.strokes.map(s=>s.length+'@'+s[0].x.toFixed(0)+','+s[0].y.toFixed(0)+'→'+s[s.length-1].x.toFixed(0)+','+s[s.length-1].y.toFixed(0));
  },
  get stage(){return stage},get first(){return cur},get steps(){return steps.length},get demo(){return demo&&{i:demo.i,of:demo.list.length}},
  get layout(){return {W,px,ox,oy,sylX,P2,O2,land,wordT}},
  get cov(){return [base,sign].map(L=>L&&{got:L.got,total:L.total,done:L.done,strokes:L.strokes.length,wide:L.wide,R:L.R})},
  strokes:()=>active()?.strokes??[],
  // One move of the board's round, for the harness's play() (core/harness.ts):
  // the board out of the picture, then the layer being asked for drawn whole.
  // False when nothing waits on a finger — off, or between layers on the
  // board's own steps, which run on game time.
  play(){
    if(stage!=='base'&&stage!=='sign')return false;
    if(!shown){showBoard(performance.now());return shown;}
    const L=active(); if(!L||L.done)return false;
    traceDebug.draw(1); return true;
  },
  draw(frac=1){
    const L=active(), t=performance.now(); if(!L)return false;
    for(const s of L.strokes){const n=Math.max(1,Math.round(s.length*frac));L.trail.push(s.slice(0,n).map(norm));for(let i=0;i<n;i++)stroke(L,s[Math.max(0,i-1)],s[i],t);}
    return L.done;
  },
};
